import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Button, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useEffect, useState } from "react";
import { formatNumber, t, type MessageKey } from "../i18n.ts";
import { ScanBar } from "../scan/ScanBar.tsx";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { productionApi } from "./api.ts";
import { activeQuery, cardQuery, PRODUCTION } from "./queries.ts";
import type { MarkAction, Step, StepState, UnitCard } from "./types.ts";

const route = getRouteApi("/app/production/my");

const STATE: Record<StepState, MessageKey> = {
  TODO: "step.TODO",
  PROGRESS: "step.PROGRESS",
  PAUSED: "step.PAUSED",
  DONE: "step.DONE",
  SKIPPED: "step.SKIPPED",
  REJECTED: "step.REJECTED",
  CANCELLED: "step.CANCELLED",
};

/** Минуты и секунды «m:ss», от часа «h:mm:ss». */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${String(h)}:${String(m).padStart(2, "0")}:${ss}` : `${String(m)}:${ss}`;
}

/** Часы раз в секунду, пока на экране карточка изделия. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/**
 * Секунды шага сейчас (МР-08): накопленное на момент ответа сервера плюс
 * сколько прошло с прихода ответа. Разница считается часами устройства, но
 * только как длительность: если они врут на минуты, таймер не съезжает.
 */
function secondsOf(step: Step | undefined, now: number, receivedAt: number): number {
  if (!step) return 0;
  const base = step.durationMinutes * 60;
  return step.state === "PROGRESS" ? base + Math.max(0, now - receivedAt) / 1000 : base;
}

/**
 * «Мої операції» для веба (chmk_route, unit_scan; Android конвейера): скан
 * изделия, свой шаг с живым таймером, «Почати», «Пауза», «Завершити»,
 * «Придатний» и «Брак» с причиной, версия на шаге прошивки (МР-15).
 * Ошибка под полем, а не диалогом.
 */
export function MyOpsPage() {
  const { serial } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const active = useQuery(activeQuery);
  const card = useQuery({ ...cardQuery(serial ?? ""), enabled: Boolean(serial) });
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [version, setVersion] = useState("");
  const mark = useMutation({ mutationFn: productionApi.mark });

  const c = card.data;
  const mine = c?.steps.find((s) => s.mine && s.state === "PROGRESS");
  const now = useNow(Boolean(c));
  const seconds = secondsOf(mine, now, card.dataUpdatedAt);

  const open = (code: string) => {
    setNotice(null);
    setReason(null);
    void navigate({ to: "/production/my", search: { serial: code }, replace: true });
  };

  const send = (step: Step | null, action: MarkAction, extra: { reason?: string; firmwareVersion?: string } = {}) => {
    if (!c) return;
    setNotice(null);
    mark.mutate(
      {
        serial: c.serial,
        stepId: step?.id ?? 0,
        action,
        deviceTime: new Date().toISOString(),
        reason: extra.reason ?? null,
        firmwareVersion: extra.firmwareVersion ?? null,
      },
      {
        onSuccess: (fresh) => {
          queryClient.setQueryData(cardQuery(c.serial).queryKey, fresh);
          void queryClient.invalidateQueries({ queryKey: PRODUCTION });
          setReason(null);
        },
        onError: (e) => setNotice(errorNotice(e)),
      },
    );
  };

  const startable = c?.steps.filter((s) => s.canStart) ?? [];
  const latest = c ? latestSteps(c) : [];
  const allDone = latest.length > 0 && latest.every((s) => s.state === "DONE" || s.state === "SKIPPED" || s.kind === "PACKING");
  const blocked = latest.find((s) => s.blocker && s.state !== "DONE" && s.kind !== "PACKING");
  // Окно «Скасувати» по часам сервера: сколько оставалось на момент ответа.
  const undoOpen = c?.undoUntil ? Date.parse(c.undoUntil) - Date.parse(c.serverNow) > now - card.dataUpdatedAt : false;
  const total = (c?.steps.reduce((a, s) => a + (latest.includes(s) ? s.durationMinutes : 0), 0) ?? 0) + (c?.earlierMinutes ?? 0);

  return (
    <div className="page stock-narrow">
      <h1 className="page-title">{t("my.title")}</h1>
      {active.data && active.data.serial !== c?.serial ? (
        <Notice tone="info" title={t("my.activeElsewhere", { step: active.data.step, serial: active.data.serial })} className="stock-notice">
          <Button onClick={() => open(active.data?.serial ?? "")}>{t("my.show")}</Button>
        </Notice>
      ) : null}
      <ScanBar onScan={open} placeholder={t("my.scan")} autoFocus={!serial} />
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {card.error ? <Notice tone="danger" title={errorNotice(card.error).title} className="stock-notice" /> : null}
      {!serial ? <p className="w-muted stock-hint">{t("my.empty")}</p> : null}

      {c ? (
        <>
          <Plate key={c.serial} cut="md" bordered className="shelf-card w-build-in">
            <div className="shelf-card-head">
              <span className="shelf-code w-num">{c.serial}</span>
            </div>
            <div className="shelf-product">{c.product}</div>
          </Plate>
          {c.quarantine ? <Notice tone="danger" title={t("my.quarantine", { reason: c.quarantineReason })} className="stock-notice" /> : null}

          <div className="my-main">
            {mine ? (
              <>
                <p className="my-timer-label">{t("my.inWork", { step: mine.name })}</p>
                <p className="my-timer w-num" role="timer" aria-live="off">
                  {clock(seconds)}
                </p>
                {mine.kind === "FIRMWARE" ? (
                  <SelectField
                    label={t("my.firmware")}
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    options={[{ value: "", label: t("my.firmwareChoose") }, ...mine.firmwareVersions.map((v) => ({ value: v, label: v }))]}
                  />
                ) : null}
                {mine.qualityCheck ? (
                  reason === null ? (
                    <div className="my-actions">
                      <Button variant="primary" size="lg" block disabled={mark.isPending} onClick={() => send(mine, "FINISH")}>
                        {t("my.pass", { step: mine.name })}
                      </Button>
                      <Button size="lg" block disabled={mark.isPending} onClick={() => setReason("")}>
                        {t("my.reject")}
                      </Button>
                    </div>
                  ) : (
                    <div className="my-actions">
                      <TextField label={t("my.rejectReason")} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                      <Button variant="primary" size="lg" block disabled={mark.isPending || !reason.trim()} onClick={() => send(mine, "REJECT", { reason })}>
                        {t("my.rejectConfirm")}
                      </Button>
                      <Button variant="ghost" onClick={() => setReason(null)}>
                        {t("common.cancel")}
                      </Button>
                    </div>
                  )
                ) : (
                  <div className="my-actions">
                    <Button
                      variant="primary"
                      size="lg"
                      block
                      disabled={mark.isPending}
                      onClick={() => send(mine, "FINISH", mine.kind === "FIRMWARE" && version ? { firmwareVersion: version } : {})}
                    >
                      {t("my.finish", { step: mine.name })}
                    </Button>
                    <Button size="lg" block disabled={mark.isPending} onClick={() => send(mine, "PAUSE")}>
                      {t("my.pause")}
                    </Button>
                  </div>
                )}
              </>
            ) : startable.length > 0 ? (
              <div className="my-actions">
                {startable.map((s, i) => (
                  <Button key={s.id} variant={i === 0 ? "primary" : "secondary"} size="lg" block disabled={mark.isPending} onClick={() => send(s, "START")}>
                    {s.state === "PAUSED" ? t("my.resume", { step: s.name, time: clock(s.durationMinutes * 60) }) : t("my.start", { step: s.name })}
                  </Button>
                ))}
              </div>
            ) : allDone ? (
              <Notice tone="success" title={t("my.routeDone")} />
            ) : blocked ? (
              <Notice tone="info" title={blocked.blocker} />
            ) : null}
            {undoOpen ? (
              // «Скасувати» живёт две минуты и гаснет при скане другого изделия (МР-07).
              <button type="button" className="advice-use my-undo" disabled={mark.isPending} onClick={() => send(null, "UNDO")}>
                {t("my.undo")}
              </button>
            ) : null}
          </div>

          <section className="stock-section" aria-labelledby="my-steps">
            <h2 id="my-steps" className="desk-group-title">
              {t("my.steps")}
            </h2>
            <ol className="steps">
              {c.steps.map((s) => (
                <li key={s.id} className="step" data-mine={s.mine ? "" : undefined} data-closed={s.state === "DONE" || s.state === "SKIPPED" || s.state === "CANCELLED" ? "" : undefined}>
                  <span className="step-name">
                    {s.name}
                    {s.attempt > 1 ? ` #${String(s.attempt)}` : ""}
                    {s.skill ? <span className="w-muted step-skill">{s.skill}</span> : null}
                    {s.firmwareVersion ? <span className="w-muted step-skill">{t("my.version", { version: s.firmwareVersion })}</span> : null}
                    {s.note ? <span className="w-muted step-skill">{s.note}</span> : null}
                  </span>
                  <span className="step-state" data-state={s.state}>
                    {t(STATE[s.state])}
                  </span>
                  <span className="w-muted step-who">{s.employee}</span>
                  <span className="w-num step-time">{s.durationMinutes > 0 || s.state === "PROGRESS" ? clock(s === mine ? seconds : s.durationMinutes * 60) : ""}</span>
                </li>
              ))}
            </ol>
            <p className="steps-total">
              {t("my.total", { minutes: formatNumber(Math.round(total * 10) / 10) })}
              {c.earlierMinutes > 0 ? ` ${t("my.withEarlier")}` : ""}
            </p>
            <Link to="/production/unit" search={{ serial: c.serial }} className="notice-link">
              {t("my.passport")}
            </Link>
          </section>
        </>
      ) : null}
    </div>
  );
}

/** Последняя попытка каждого шага: по ней ясно, что дальше. */
function latestSteps(c: UnitCard): Step[] {
  const by = new Map<string, Step>();
  for (const s of c.steps) {
    const prev = by.get(s.name);
    if (!prev || s.attempt > prev.attempt) by.set(s.name, s);
  }
  return [...by.values()];
}
